/**
 * product.js
 * Handles:
 *  1. Variant selection (color swatches, size chips, select fallbacks)
 *     - Finds matching variant from product JSON
 *     - Dispatches `variant:change` on the section element
 *  2. Listeners on `variant:change`:
 *     - Updates hidden variant ID input
 *     - Syncs price display
 *     - Enables/disables ATC button
 *     - Replaces main gallery image when variant has its own featured_image
 *     - Updates URL via history.replaceState
 *  3. Quantity stepper (+/- buttons)
 *  4. Add-to-cart via /cart/add.js + dispatches `cart:updated` on document
 *  5. Sticky ATC bar (mobile, IntersectionObserver on main ATC button)
 *
 * Task: K-057  Phase 4 final
 */

(function () {
  'use strict';

  /* ── Utility: find a variant matching the current option selections ─────── */

  function findMatchingVariant(product, selectedOptions) {
    return product.variants.find(function (variant) {
      return variant.options.every(function (opt, i) {
        return opt === selectedOptions[i];
      });
    }) || null;
  }

  /* ── Utility: money format ──────────────────────────────────────────────── */

  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.money_format) {
      var formatted = window.Shopify.money_format.replace(/\{\{.*?\}\}/, function () {
        return (cents / 100).toFixed(2);
      });
      return formatted;
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Variant picker initialisation ────────────────────────────────────────── */

  function initVariantPicker(section) {
    var picker = section.querySelector('[data-variant-picker]');
    if (!picker) return;

    var productJson = picker.dataset.productJson;
    if (!productJson) return;

    var product;
    try {
      product = JSON.parse(productJson);
    } catch (e) {
      return;
    }

    var sectionId = picker.dataset.sectionId;
    var variantInput = section.querySelector('input[name="id"]');
    if (!variantInput) return;

    /* Track currently selected option values keyed by position (1-indexed) */
    var selectedOptions = [];

    /* Populate initial selections from checked/selected inputs */
    picker.querySelectorAll('[data-option-position]').forEach(function (el) {
      var position = parseInt(el.dataset.optionPosition, 10) - 1;
      if (el.tagName === 'SELECT') {
        selectedOptions[position] = el.value;
      } else if (el.type === 'radio' && el.checked) {
        selectedOptions[position] = el.value;
      }
    });

    function handleOptionChange(optionPosition, newValue) {
      selectedOptions[optionPosition - 1] = newValue;

      var matched = findMatchingVariant(product, selectedOptions);

      /* Dispatch variant:change on the section for all listeners */
      section.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: matched, product: product }
      }));
    }

    /* Radio inputs (swatches + chips) */
    picker.querySelectorAll('input[type="radio"][data-option-position]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        var position = parseInt(this.dataset.optionPosition, 10);

        /* Update selected label for color option (capitalize for "Color: Owl") */
        var group = picker.querySelector('[data-option-group="' + (position - 1) + '"]');
        if (group) {
          var valueDisplay = group.querySelector('[data-option-selected-value]');
          if (valueDisplay) {
            var rawValue = this.value;
            var optionLabel = group.querySelector('.variant-picker__option-label');
            var isColorOpt = group.classList.contains('variant-picker__option--color');
            var displayValue = isColorOpt
              ? rawValue.charAt(0).toUpperCase() + rawValue.slice(1).toLowerCase()
              : rawValue;
            valueDisplay.textContent = displayValue;
            valueDisplay.dataset.optionSelectedValue = rawValue;
            void optionLabel; /* reserved for future label tweaks */
          }
          /* Toggle is-selected class on sibling labels */
          group.querySelectorAll('.variant-picker__swatch, .variant-picker__chip').forEach(function (lbl) {
            lbl.classList.remove('is-selected');
          });
          this.parentElement.classList.add('is-selected');
        }

        handleOptionChange(position, this.value);
      });
    });

    /* Select fallbacks */
    picker.querySelectorAll('select[data-option-position]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var position = parseInt(this.dataset.optionPosition, 10);
        handleOptionChange(position, this.value);
      });
    });
  }

  /* ── variant:change listeners ──────────────────────────────────────────── */

  function initVariantListeners(section) {
    section.addEventListener('variant:change', function (e) {
      var variant = e.detail.variant;

      /* 1. Hidden variant ID input */
      var variantInput = section.querySelector('input[name="id"]');
      if (variantInput && variant) {
        variantInput.value = variant.id;
      }

      /* 2. Price update */
      syncPrice(section, variant);

      /* 3. ATC button state */
      syncAtcButton(section, variant);

      /* 4. Sticky ATC button state — sticky bar listens to variant:change
         itself (see initStickyAtc). No-op here. */

      /* 4b. Urgency message — re-evaluate per-variant stock */
      syncUrgency(section, variant);

      /* 5. Gallery image swap */
      if (variant && variant.featured_image) {
        swapGalleryImage(section, variant.featured_image);
      }

      /* 6. URL update */
      if (variant) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({ variantId: variant.id }, '', url.toString());
      }
    });
  }

  function syncPrice(section, variant) {
    var priceEl = section.querySelector('.product-price');
    if (!priceEl) return;

    if (!variant) {
      priceEl.querySelector('.product-price__current') && (priceEl.querySelector('.product-price__current').textContent = '—');
      return;
    }

    var currentEl = priceEl.querySelector('.product-price__current');
    var compareEl = priceEl.querySelector('.product-price__compare-at');
    var discountEl = priceEl.querySelector('.product-price__discount');

    if (currentEl) currentEl.textContent = formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (discountEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        var pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        discountEl.textContent = '(' + pct + '% OFF)';
        discountEl.hidden = false;
      } else {
        discountEl.hidden = true;
      }
    }
  }

  function syncAtcButton(section, variant) {
    var atcBtn = section.querySelector('.product__atc');
    if (!atcBtn) return;

    var available = variant && variant.available;
    atcBtn.disabled = !available;

    var labelEl = atcBtn.querySelector('.button__label');
    if (labelEl) {
      if (!variant) {
        labelEl.textContent = atcBtn.dataset.soldOutText || 'Sold out';
      } else if (!available) {
        labelEl.textContent = atcBtn.dataset.soldOutText || 'Sold out';
      } else {
        labelEl.textContent = atcBtn.dataset.addToCartText || 'Add to bag';
      }
    }

    var stickyBtn = section.querySelector('.product-sticky-atc__btn');
    if (stickyBtn) {
      stickyBtn.disabled = !available;
    }
  }

  /* Re-evaluate urgency state for the currently-selected variant.
     Initial server render bakes the message based on `selected_or_first_available_variant`
     — but on variant switch we need to re-check `inventory_management` and
     `inventory_quantity` client-side so the message hides on untracked variants
     and updates between sold-out / low-stock / hidden states. */
  function syncUrgency(section, variant) {
    var urgencyBlock = section.querySelector('.product__block--urgency');
    if (!urgencyBlock || !variant) return;

    /* Threshold matches Liquid render in sections/product.liquid (urgency block) */
    var LOW_STOCK_THRESHOLD = 5;

    /* Shopify's `{{ product | json }}` filter strips inventory_quantity, so
       we read it from a parallel stock map emitted by variant-picker.liquid:
       data-variant-stock = { variantId: { mgmt, qty, available } }. */
    var picker = section.querySelector('[data-variant-stock]');
    var stockMap = {};
    if (picker) {
      try { stockMap = JSON.parse(picker.dataset.variantStock); } catch (e) {}
    }
    var stock = stockMap[variant.id] || {};
    var tracked = !!stock.mgmt;
    var qty = stock.qty;
    var available = stock.available !== undefined ? stock.available : variant.available;

    var soldOut = tracked && available === false;
    var lowStock = tracked && qty > 0 && qty <= LOW_STOCK_THRESHOLD;

    var soldOutText = urgencyBlock.dataset.soldOutText || 'This product is currently out of stock';
    var lowStockText = urgencyBlock.dataset.lowStockText || 'Only Few Left!';

    /* Remove any existing urgency paragraph; rebuild only when needed. */
    var existing = urgencyBlock.querySelector('.product__urgency');
    if (existing) existing.remove();

    if (!soldOut && !lowStock) return;

    var p = document.createElement('p');
    p.className = soldOut ? 'product__urgency product__urgency--sold-out' : 'product__urgency';
    var span = document.createElement('span');
    span.className = 'product__urgency-text';
    span.textContent = soldOut ? soldOutText : lowStockText;
    p.appendChild(span);
    urgencyBlock.appendChild(p);
  }

  function swapGalleryImage(section, featuredImage) {
    var mainImg = section.querySelector('.product-media__primary-img');
    if (!mainImg || !featuredImage) return;
    /* Use the raw CDN src — Shopify variant featured_image.src is already a
       full CDN URL. Clear srcset so browser doesn't serve a stale candidate. */
    mainImg.srcset = '';
    mainImg.src = featuredImage.src;
    if (featuredImage.alt) mainImg.alt = featuredImage.alt;
  }

  /* ── Quantity stepper ──────────────────────────────────────────────────── */

  function initQuantityStepper(section) {
    var input = section.querySelector('.product__qty-input');
    if (!input) return;

    section.querySelector('[data-qty-minus]') && section.querySelector('[data-qty-minus]').addEventListener('click', function () {
      var val = parseInt(input.value, 10) || 1;
      if (val > 1) input.value = val - 1;
    });

    section.querySelector('[data-qty-plus]') && section.querySelector('[data-qty-plus]').addEventListener('click', function () {
      var val = parseInt(input.value, 10) || 1;
      input.value = val + 1;
    });
  }

  /* ── Add-to-cart ─────────────────────────────────────────────────────────── */

  function initAtcForm(section) {
    var form = section.querySelector('.product__form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var atcBtn = form.querySelector('.product__atc');
      if (atcBtn && atcBtn.disabled) return;

      /* Read variant ID and quantity directly — avoids Shopify hidden fields
         (form_type, utf8) from being sent and potentially confusing the endpoint. */
      var variantInput = form.querySelector('input[name="id"]');
      var qtyInput = form.querySelector('[data-qty-input], [name="quantity"], .product__qty-input');
      var variantId = variantInput ? variantInput.value : '';
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      if (!variantId) {
        console.error('[product.js] ATC: no variant id found in form');
        return;
      }

      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.classList.add('is-loading');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || 'Cart add failed (' + res.status + ')');
            });
          }
          return res.json();
        })
        .then(function () {
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          /* Standing rule: dispatch cart:updated so header badge refreshes,
             and cart:item-added so cart drawer auto-opens (K-061 contract). */
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: cart }));
        })
        .catch(function (err) {
          console.error('[product.js] cart/add error:', err.message || err);
        })
        .finally(function () {
          if (atcBtn) {
            atcBtn.disabled = false;
            atcBtn.classList.remove('is-loading');
          }
        });
    });
  }

  /* ── Sticky mobile Buy/Qty bar ──────────────────────────────────────────
     Always-visible bottom-fixed bar on mobile (CSS handles the breakpoint).
     The bar's qty <select> mirrors the inline qty so both stay in sync; the
     bar's button calls form.requestSubmit() on the main in-page product
     form so the existing ATC flow (cart/add.js, cart:updated dispatch) is
     reused without duplication. */

  function initStickyAtc(section) {
    var stickyBar = section.querySelector('[data-sticky-atc]');
    if (!stickyBar) return;

    var mainForm = section.querySelector('.product__form');
    var mainQty = section.querySelector('[data-qty-input]');
    var stickyQty = stickyBar.querySelector('[data-sticky-qty]');
    var stickyBtn = stickyBar.querySelector('[data-sticky-atc-submit]');

    /* Two-way sync between sticky qty and inline qty so the user can choose
       either control and the other stays in sync. */
    if (stickyQty && mainQty) {
      stickyQty.addEventListener('change', function () {
        mainQty.value = stickyQty.value;
      });
      mainQty.addEventListener('change', function () {
        if (stickyQty.value !== mainQty.value) {
          /* Only assign if a matching option exists; otherwise leave the
             sticky select untouched (e.g. inline allows higher max). */
          var opt = stickyQty.querySelector('option[value="' + mainQty.value + '"]');
          if (opt) stickyQty.value = mainQty.value;
        }
      });
    }

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (mainForm) mainForm.requestSubmit();
      });
    }

    /* Keep the sticky button label/state in sync with the inline ATC button. */
    section.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!stickyBtn) return;
      var available = variant && variant.available;
      stickyBtn.disabled = !available;
      var labelEl = stickyBtn.querySelector('.button__label');
      if (labelEl) {
        labelEl.textContent = available
          ? (stickyBtn.dataset.addToCartText || 'Add to bag')
          : (stickyBtn.dataset.soldOutText || 'Sold out');
      }
    });

    /* Smart-sticky: only show the bottom bar when the inline ATC button is
       OUT of view. Hide it when the user can see/tap the inline ATC — avoids
       a redundant overlay when both buttons would be visible at once. */
    var mainAtc = section.querySelector('.product__atc');
    if (mainAtc && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        stickyBar.classList.toggle('is-hidden', entry.isIntersecting);
      }, {
        /* Hide once 50% of the inline ATC is in view; smaller threshold
           would flicker at the edge of the viewport. */
        threshold: 0.5
      });
      io.observe(mainAtc);
      /* Initial state: hidden until first scroll callback fires. Prevents
         the bar from flashing in then sliding away on page load. */
      stickyBar.classList.add('is-hidden');
    }
  }

  /* ── Size guide trigger: hide when no size guide is linked ────────────── */

  function initSizeGuideTrigger(section) {
    var noGuide = section.querySelector('[data-no-size-guide]');
    if (!noGuide) return;

    var trigger = section.querySelector('[data-modal-open="size-guide"]');
    if (trigger) {
      var block = trigger.closest('.product__block--size-guide');
      if (block) block.hidden = true;
    }
  }

  /* ── #reviews hash → open the accordion ───────────────────────────────────
     The product-rating count link points at #reviews (a <details> element).
     Default browser behaviour is to scroll-into-view but leave the accordion
     closed. Open it programmatically on hash match (both on initial load and
     on subsequent in-page hash changes). */
  function openReviewsIfHashed() {
    if (window.location.hash !== '#reviews') return;
    var d = document.getElementById('reviews');
    if (d && d.tagName === 'DETAILS') d.open = true;
  }

  document.addEventListener('DOMContentLoaded', openReviewsIfHashed);
  window.addEventListener('hashchange', openReviewsIfHashed);

  /* ── Bootstrap — one section at a time ────────────────────────────────── */

  function initProductSection(section) {
    initVariantPicker(section);
    initVariantListeners(section);
    initQuantityStepper(section);
    initAtcForm(section);
    initStickyAtc(section);
    initSizeGuideTrigger(section);
  }

  document.querySelectorAll('[id^="product-"]').forEach(function (section) {
    if (section.hasAttribute('aria-label')) {
      initProductSection(section);
    }
  });

})();
